import { useState } from "react";
import { responseHero } from "../../../../utils/axiosInterceptor";
import { toast } from "react-toastify";
import { Group } from "../../../../models/group.model";
import { Crypto } from "../../../../models/crypto.model";
import { useSelector } from "react-redux";
import { selectUser } from "../../../../store/reducers/userSlice";
import React from "react";
import { CryptoDeposit } from "../../../../models/cryptodeposit.model";

type ModalProps = {
    title: string;
    group: Group | undefined;
    showModal: boolean;
    closeModal: () => void;
    handleStateChange: () => void;
    asset_id: number | undefined;
}

type ModifiedPayload = Pick<Crypto, 
"date" |
"note" |
"operation" |
"quantity" |
"exchange_rate" |
"amount" |
"percentage_to_alt" |
"percentage_to_gold" |
"percentage_to_mining" |
"creatorId" |
"robotFlag">

const AddNewRecordModal = ({ group, title, showModal, closeModal, handleStateChange, asset_id } : ModalProps) => {

    const user = useSelector(selectUser)
    const [operation, setOperation] = useState(true); // BUY
    const [isRobot, setIsRobot] = useState(false);
    const [depositRegistrations, setDepositRegistrations] = useState<CryptoDeposit[]>([])

    const [recordData, setRecordData] = useState<ModifiedPayload>({
        date: new Date(),
        operation: true,
        quantity: "",
        exchange_rate: "",
        note: "",
        amount: 0,
        percentage_to_alt: 0,
        percentage_to_gold: 0,
        percentage_to_mining: 0,
        creatorId: user.userInfo.user_id,
        robotFlag: false, // Initialized as false
    });

    const handleRobotToggle = (checked: boolean) => {
        setIsRobot(checked);
        if (checked) {
            setOperation(true); // Force BUY UI
            setRecordData((prev) => ({
                ...prev,
                operation: true,
                note: "ROBOT",
                exchange_rate: "0", 
                amount: 0,
                quantity: "",
                robotFlag: true, // Set flag to true
            }));
        } else {
            setRecordData((prev) => ({ 
                ...prev, 
                note: "", 
                quantity: "", 
                robotFlag: false // Revert flag
            }));
        }
    };

    const handleChange = (
        type: keyof ModifiedPayload,
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        const inputValue = e.target.value;
    
        switch (type) {
            case "operation":
                setOperation((prev) => !prev);
                setRecordData((prevData) => ({
                    ...prevData,
                    [type]: !prevData.operation,
                }));
                break;
    
            case "exchange_rate":
                if (/^\d*\.?\d{0,4}$/.test(inputValue)) {
                    setRecordData((prevData) => ({ ...prevData, [type]: inputValue }));
                }
                break;
    
            case "quantity":
                if (/^\d*\.?\d{0,8}$/.test(inputValue)) {
                    setRecordData((prevData) => ({ ...prevData, [type]: inputValue }));
                }
                break;
    
            case "date":
            case "note":
                setRecordData((prevData) => ({ ...prevData, [type]: inputValue }));
                break;

            case "robotFlag":
                // Handled specifically by handleRobotToggle
                break;
    
            default:
                const parsedValue = inputValue === "" ? 0 : parseInt(inputValue, 10);
                setRecordData((prevData) => ({
                    ...prevData,
                    [type]: parsedValue,
                }));
                break;
        }
    };

    const makeRecord = async () => {
        if(!group) return;
        // Sending recordData which now contains the robotFlag
        await responseHero.post(`${process.env.REACT_APP_API_URL}/humandc/crypto/${group.group_id}/${asset_id}`, { record: recordData }).then((response) => {
            closeModal()
            handleStateChange();
            toast.success(`Sikeresen hozzáadtál egy rekordot!`);
        }).catch(error => {
            console.error(JSON.stringify(error.response.data.message, null, 2));
            closeModal()
            toast.error(`Sikertelen adatrögzítés!`);
        })
    }

    return (
    <>
        {showModal && (
            <>
            <div className="flex justify-center items-center overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
                <div className="relative w-full my-6 mx-auto max-w-4xl">
                    <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
                        <div className="flex items-start justify-between p-5 border-b border-solid border-gray-300 rounded-t">
                            <h3 className="text-xl text-brown font-semibold"><span className="text-dark-blue">HUMAN D.C</span> - {title}</h3>
                        </div>
                        <div className="relative p-6 flex-auto">
                            <form>
                            <div className="flex p-4 mb-4 bg-blue-50 rounded shadow-sm items-center justify-center border border-blue-200">
                                <label className="inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={isRobot}
                                        onChange={(e) => handleRobotToggle(e.target.checked)}
                                        className="form-checkbox h-5 w-5 text-dark-blue rounded"
                                    />
                                    <span className="ml-3 font-bold text-dark-blue uppercase tracking-wide">
                                        Ez egy ROBOT termelési adat
                                    </span>
                                </label>
                            </div>

                            {!isRobot && (
                                <div className="flex p-2 rounded shadow-md">
                                    <div className="w-full m-auto pr-2 text-center items-center">
                                        <label className="block text-sm font-bold mt-5 text-brown text-center justify-center">
                                            <span className="text-brown">Típus </span>
                                        </label>
                                        <div className="flex justify-center mt-2">
                                            <label className="inline-flex items-center">
                                                <input
                                                    type="radio"
                                                    checked={operation === true}
                                                    onChange={(e) => handleChange('operation', e)}
                                                    className="form-radio h-5 w-5 text-brown"
                                                />
                                                <span className="ml-2 text-success font-bold">BUY</span>
                                            </label>
                                            <label className="inline-flex items-center ml-6">
                                                <input
                                                    type="radio"
                                                    checked={operation === false}
                                                    onChange={(e) => handleChange('operation', e)}
                                                    className="form-radio h-5 w-5 text-brown"
                                                />
                                                <span className="ml-2 text-error font-bold">SELL</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="flex p-2 rounded shadow-md">
                                <div className="w-1/2 pr-2 text-center items-center">
                                    <label className="block text-sm font-bold mt-5 text-brown text-center justify-center">
                                        <span className="text-brown">Dátum </span>
                                    </label>
                                    <input
                                        value={recordData?.date?.toString().split('T')[0]}
                                        onChange={(e) => handleChange('date', e)}
                                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline" type="date"
                                    />
                                </div>

                                <div className="w-1/2 pr-2 text-center items-center">
                                    <label className="block text-sm font-bold mt-5 text-brown text-center justify-center">
                                        <span className="text-brown">Megjegyzés</span>
                                    </label>
                                    <input
                                        value={recordData?.note}
                                        disabled={isRobot}
                                        onChange={(e) => handleChange('note', e)}
                                        className={`shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${isRobot ? 'bg-gray-100 italic' : ''}`}
                                        type="text"
                                        placeholder="Megjegyzés (pl. ROBOT)"
                                    />
                                </div>
                            </div>

                            <div className="flex p-2 rounded shadow-md">
                                {isRobot ? (
                                    <div className="w-full pr-2 text-center justify-center items-center">
                                        <label className="block text-sm font-bold mt-5 text-dark-blue text-center justify-center">
                                            <span className="uppercase">Termelt coinok mennyisége</span> <br/>(8 tizedesjegyig)
                                        </label>
                                        <input
                                            value={recordData?.quantity}
                                            onChange={(e) => handleChange('quantity', e)}
                                            className="shadow appearance-none border border-dark-blue rounded w-full py-2 px-3 text-gray-700 font-mono leading-tight focus:outline-none focus:shadow-outline"
                                            type="number"
                                            step="0.00000001"
                                            placeholder="77722222.88888888"
                                        />
                                    </div>
                                ) : (
                                    <>
                                        <div className="w-1/2 pr-2 text-center justify-center items-center">
                                            <label className="block text-sm font-bold mt-5 text-brown text-center justify-center">
                                                <span className="text-success">Árfolyam </span> <br/>USDT
                                            </label>
                                            <input
                                                value={recordData?.exchange_rate}
                                                onChange={(e) => handleChange('exchange_rate', e)}
                                                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                                type="number"
                                                placeholder="Árfolyam USDT-ben"
                                            />
                                        </div>

                                        {operation ? (
                                            <div className="w-2/4 pr-2 text-center justify-center items-center">
                                                <label className="block text-sm font-bold mt-5 text-brown text-center justify-center">
                                                    <span className="text-success">Összeg </span> <br/>USDT
                                                </label>
                                                <input
                                                    value={recordData?.amount}
                                                    onChange={(e) => handleChange('amount', e)}
                                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                                    type="number"
                                                    placeholder="Összeg USDT-ben"
                                                />
                                            </div>
                                        ) : (
                                            <div className="w-2/4 pr-2 text-center justify-center items-center">
                                                <label className="block text-sm font-bold mt-5 text-brown text-center justify-center">
                                                    <span className="text-success">Kriptoeszköz </span> <br/>Darabszám
                                                </label>
                                                <input
                                                    value={recordData?.quantity}
                                                    onChange={(e) => handleChange('quantity', e)}
                                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                                    type="text"
                                                    placeholder="Kriptoeszköz darabszáma"
                                                />
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>

                            {!operation && (
                                <div className="flex p-2 rounded shadow-md">
                                    <div className="w-2/4 pr-2 text-center items-center">
                                        <label className="block text-sm font-bold mt-5 text-brown text-center justify-center">
                                            <span className="text-success">Százalék </span> <br/>Arany
                                        </label>
                                        <input
                                            value={recordData?.percentage_to_gold}
                                            onChange={(e) => handleChange('percentage_to_gold', e)}
                                            min="0" max="100" className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline" type="number"
                                        />
                                    </div>
                                    <div className="w-2/4 pr-2 text-center items-center">
                                        <label className="block text-sm font-bold mt-5 text-brown text-center justify-center">
                                            <span className="text-success">Százalék </span> <br/>Bányagép-energia
                                        </label>
                                        <input
                                            value={recordData?.percentage_to_mining}
                                            onChange={(e) => handleChange('percentage_to_mining', e)}
                                            min="0" max="100" className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline" type="number"
                                        />
                                    </div>
                                    <div className="w-2/4 pr-2 text-center items-center">
                                        <label className="block text-sm font-bold mt-5 text-brown text-center justify-center">
                                            <span className="text-success">Százalék </span> <br/>Altcoin
                                        </label>
                                        <input
                                            value={recordData?.percentage_to_alt}
                                            onChange={(e) => handleChange('percentage_to_alt', e)}
                                            min="0" max="100" className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline" type="number"
                                        />
                                    </div>
                                </div>
                            )}
                            </form>
                        </div>
                        <div className="flex items-center justify-end p-6 border-t border-solid border-blueGray-200 rounded-b">
                            <button
                                className="text-red-500 background-transparent font-bold uppercase px-6 py-2 text-sm outline-none focus:outline-none mr-1 mb-1"
                                type="button"
                                onClick={closeModal}
                            >
                                Mégse
                            </button>
                            <button
                                className="text-cream bg-dark-blue font-bold uppercase text-sm px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1"
                                type="button"
                                onClick={makeRecord}
                            >
                                Hozzáad
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-filter backdrop-blur-sm"></div>
            </>
        )}
    </>
  );
};

export default AddNewRecordModal;